/**
 * What can be done to documents in Client Documents, with the File Groups
 * the backend already has: add to a group, move between groups (add, then
 * remove), remove from a group, download. Nothing here renames or deletes a
 * file — the backend has no such endpoint, so the UI does not pretend to.
 */
import { useState } from 'react';
import {
	Box,
	Button,
	IconButton,
	ListSubheader,
	Menu,
	MenuItem,
	Tooltip,
	Typography,
} from '@mui/material';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import { useChangeGroupMembers } from '../../queries/fileGroups';
import { useToast } from '../../provider/toast';
import { count } from '../../utils/format';
import { downloadFile } from '../files/FileTable';
import type { FileGroupRecord } from '../../schemas';
import { withAdded, withRemoved } from './membership';

const documents = (n: number) => `${count(n)} document${n === 1 ? '' : 's'}`;
const why = (error: unknown) =>
	error instanceof Error ? error.message : undefined;

export function useGroupActions(clientId: string, groups: FileGroupRecord[]) {
	const change = useChangeGroupMembers(clientId);
	const toast = useToast();
	const [moving, setMoving] = useState(false);
	const nameOf = (id: string) =>
		groups.find((group) => group.pkFileGroupId === id)?.name ?? 'the group';

	const add = async (ids: string[], to: string) => {
		try {
			await change.mutateAsync({
				groupId: to,
				change: (current) => withAdded(current, ids),
			});
			toast('ok', `Added ${documents(ids.length)} to ${nameOf(to)}`);
			return true;
		} catch (error) {
			toast('danger', `Could not add to ${nameOf(to)}`, why(error));
			return false;
		}
	};

	const remove = async (ids: string[], from: string) => {
		try {
			await change.mutateAsync({
				groupId: from,
				change: (current) => withRemoved(current, ids),
			});
			toast(
				'ok',
				`Removed ${documents(ids.length)} from ${nameOf(from)}`,
			);
			return true;
		} catch (error) {
			toast(
				'danger',
				`Could not remove from ${nameOf(from)}`,
				why(error),
			);
			return false;
		}
	};

	/* Two saves. If the second fails the documents are in both groups, and the
     message says exactly that rather than claiming a move happened. */
	const move = async (ids: string[], from: string, to: string) => {
		setMoving(true);
		try {
			try {
				await change.mutateAsync({
					groupId: to,
					change: (current) => withAdded(current, ids),
				});
			} catch (error) {
				toast('danger', `Could not move to ${nameOf(to)}`, why(error));
				return false;
			}
			try {
				await change.mutateAsync({
					groupId: from,
					change: (current) => withRemoved(current, ids),
				});
				toast('ok', `Moved ${documents(ids.length)} to ${nameOf(to)}`);
				return true;
			} catch (error) {
				toast(
					'danger',
					`Added to ${nameOf(to)}, but could not remove from ${nameOf(from)}`,
					`They are now in both groups. ${why(error) ?? ''}`.trim(),
				);
				return false;
			}
		} finally {
			setMoving(false);
		}
	};

	return { add, remove, move, busy: change.isPending || moving };
}

/** One original after another; a short gap so the browser keeps every download. */
export async function downloadMany(ids: string[]): Promise<number> {
	let failed = 0;
	for (const [index, id] of ids.entries()) {
		if (index > 0) await new Promise((resolve) => setTimeout(resolve, 400));
		try {
			await downloadFile(id);
		} catch {
			failed += 1;
		}
	}
	return failed;
}

function GroupPicker({
	label,
	groups,
	disabled,
	onPick,
}: {
	label: string;
	groups: FileGroupRecord[];
	disabled?: boolean;
	onPick: (groupId: string) => void;
}) {
	const [anchor, setAnchor] = useState<HTMLElement | null>(null);
	return (
		<>
			<Button
				size="small"
				variant="outlined"
				endIcon={<ArrowDropDownIcon />}
				disabled={disabled}
				aria-haspopup="menu"
				aria-expanded={Boolean(anchor)}
				onClick={(event) => setAnchor(event.currentTarget)}
			>
				{label}
			</Button>
			<Menu
				anchorEl={anchor}
				open={Boolean(anchor)}
				onClose={() => setAnchor(null)}
			>
				{groups.length === 0 ? (
					<MenuItem disabled>Already in every group</MenuItem>
				) : (
					groups.map((group) => (
						<MenuItem
							key={group.pkFileGroupId}
							onClick={() => {
								setAnchor(null);
								onPick(group.pkFileGroupId);
							}}
						>
							{group.name}
						</MenuItem>
					))
				)}
			</Menu>
		</>
	);
}

export function SelectionActions({
	count: picked,
	groups,
	currentGroupId,
	canGroup,
	busy,
	downloading,
	onAdd,
	onMove,
	onRemove,
	onDownload,
	onClear,
}: {
	count: number;
	groups: FileGroupRecord[];
	currentGroupId: string | null;
	canGroup: boolean;
	busy: boolean;
	downloading?: boolean;
	onAdd: (to: string) => void;
	onMove: (to: string) => void;
	onRemove: () => void;
	onDownload: () => void;
	onClear: () => void;
}) {
	const others = groups.filter(
		(group) => group.pkFileGroupId !== currentGroupId,
	);
	return (
		<Box
			role="toolbar"
			aria-label="Selected documents"
			sx={{
				display: 'flex',
				alignItems: 'center',
				gap: 'var(--s2)',
				flexWrap: 'wrap',
				minHeight: 'var(--ctl-h)',
			}}
		>
			<Typography
				sx={{
					fontSize: 'var(--fs-body)',
					fontWeight: 600,
					color: 'var(--accent)',
				}}
			>
				{documents(picked)} selected
			</Typography>
			{canGroup && (
				<GroupPicker
					label="Add to group"
					groups={others}
					disabled={busy}
					onPick={onAdd}
				/>
			)}
			{canGroup && currentGroupId && (
				<GroupPicker
					label="Move to"
					groups={others}
					disabled={busy}
					onPick={onMove}
				/>
			)}
			{canGroup && currentGroupId && (
				<Button
					size="small"
					variant="outlined"
					disabled={busy}
					onClick={onRemove}
				>
					Remove from group
				</Button>
			)}
			<Button
				size="small"
				variant="outlined"
				disabled={downloading}
				loading={downloading}
				onClick={onDownload}
			>
				Download
			</Button>
			<Button size="small" variant="text" onClick={onClear}>
				Clear selection
			</Button>
		</Box>
	);
}

export function RowActionsMenu({
	fileName,
	groups,
	currentGroupId,
	busy,
	onAdd,
	onMove,
	onRemove,
	showGroups = true,
	onDelete,
}: {
	fileName: string;
	groups: FileGroupRecord[];
	currentGroupId: string | null;
	busy: boolean;
	onAdd: (to: string) => void;
	onMove: (to: string) => void;
	onRemove: () => void;
	/** False when there are no groups to add to: only the other items show. */
	showGroups?: boolean;
	/** Given for a user who may delete files (`deleteData`). */
	onDelete?: () => void;
}) {
	const [anchor, setAnchor] = useState<HTMLElement | null>(null);
	const others = groups.filter(
		(group) => group.pkFileGroupId !== currentGroupId,
	);
	const close = () => setAnchor(null);
	const pick = (run: (id: string) => void, id: string) => () => {
		close();
		run(id);
	};
	/* MUI Menu wants direct children, not fragments: build one flat list. */
	const groupEntries = [
		<ListSubheader key="add-h">Add to group</ListSubheader>,
		...(others.length === 0
			? [
					<MenuItem key="add-none" disabled>
						Already in every group
					</MenuItem>,
				]
			: others.map((group) => (
					<MenuItem
						key={`add-${group.pkFileGroupId}`}
						disabled={busy}
						onClick={pick(onAdd, group.pkFileGroupId)}
					>
						{group.name}
					</MenuItem>
				))),
		...(currentGroupId && others.length > 0
			? [
					<ListSubheader key="move-h">Move to</ListSubheader>,
					...others.map((group) => (
						<MenuItem
							key={`move-${group.pkFileGroupId}`}
							disabled={busy}
							onClick={pick(onMove, group.pkFileGroupId)}
						>
							{group.name}
						</MenuItem>
					)),
				]
			: []),
		...(currentGroupId
			? [
					<MenuItem
						key="remove"
						disabled={busy}
						onClick={() => {
							close();
							onRemove();
						}}
						sx={{ borderTop: '1px solid var(--line)', mt: '4px' }}
					>
						Remove from this group
					</MenuItem>,
				]
			: []),
	];
	const entries = [
		...(showGroups ? groupEntries : []),
		...(onDelete
			? [
					/* Not tied to `busy`: that waits on group membership, which a delete
             does not need. */
					<MenuItem
						key="delete"
						onClick={() => {
							close();
							onDelete();
						}}
						sx={{
							color: 'error.main',
							...(showGroups
								? {
										borderTop: '1px solid var(--line)',
										mt: '4px',
									}
								: {}),
						}}
					>
						Delete file…
					</MenuItem>,
				]
			: []),
	];
	return (
		<>
			<Tooltip title="More actions">
				<IconButton
					size="small"
					aria-label={`More actions for ${fileName}`}
					aria-haspopup="menu"
					onClick={(event) => {
						event.stopPropagation();
						setAnchor(event.currentTarget);
					}}
				>
					<MoreVertIcon fontSize="small" />
				</IconButton>
			</Tooltip>
			<Menu
				anchorEl={anchor}
				open={Boolean(anchor)}
				onClose={close}
				onClick={(event) => event.stopPropagation()}
				slotProps={{ paper: { sx: { minWidth: 200, maxHeight: 360 } } }}
			>
				{entries}
			</Menu>
		</>
	);
}
